import React, { useCallback, useEffect, useState } from 'react';
import {
  Row, Col, Card, Table, Button, Input, Select, Spin, Tag, Modal,
  Statistic, Tooltip, notification, Divider, Badge
} from 'antd';
import {
  SearchOutlined, FileExcelOutlined, EyeOutlined,
  CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { baseUrl } from '../../config';
import { usePaymentDropdowns } from '../../hooks/usePaymentDropdowns';
import '../../assets/styles/custom_style.css';

const { Option } = Select;

const hdr = text => (
  <span style={{ fontSize: 12, color: '#7a0000', fontWeight: 700 }}>{text}</span>
);

// Computes a gap/paid timeline from sorted payment rows
function buildTimeline(payments) {
  const timeline = [];
  payments.forEach((p, idx) => {
    timeline.push({ type: 'paid', data: p });
    if (idx < payments.length - 1) {
      const endM  = moment(p.end_month);
      const nextM = moment(payments[idx + 1].start_month);
      // gap = months between end of current and start of next (exclusive of both boundary months)
      const gapMonths = nextM.diff(endM, 'months') - 1;
      if (gapMonths > 0) {
        timeline.push({
          type: 'gap',
          from: moment(p.end_month).add(1, 'month').format('MMM-YYYY'),
          to:   moment(payments[idx + 1].start_month).subtract(1, 'month').format('MMM-YYYY'),
          months: gapMonths,
        });
      }
    }
  });
  // Overdue check — months after last end_month up to today
  if (payments.length > 0) {
    const lastEnd     = moment(payments[payments.length - 1].end_month);
    const today       = moment();
    const overdueMonths = today.diff(lastEnd, 'months');
    if (overdueMonths > 0) {
      timeline.push({
        type:   'overdue',
        from:   moment(lastEnd).add(1, 'month').format('MMM-YYYY'),
        to:     today.format('MMM-YYYY'),
        months: overdueMonths,
      });
    }
  }
  return timeline;
}

export default function ContinuityReport() {
  const { bankAccounts } = usePaymentDropdowns();

  const [data,       setData]       = useState([]);
  const [summary,    setSummary]    = useState({});
  const [loading,    setLoading]    = useState(false);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [limit,      setLimit]      = useState(50);

  const [filters, setFilters] = useState({
    name: '', phone: '', village: '', district: '',
    bank_account: '', frequency_type: '', member_status: '',
    continuity_status: '', has_unverified: '', min_months: '',
  });

  // Gap modal state
  const [gapModal,     setGapModal]     = useState(false);
  const [gapLoading,   setGapLoading]   = useState(false);
  const [gapTimeline,  setGapTimeline]  = useState([]);
  const [gapMember,    setGapMember]    = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  const fetchData = useCallback(async (pg = page, lmt = limit, flt = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: lmt, ...flt });
      const res = await axios.get(`${baseUrl}/reports/payment-continuity?${params}`, { headers });
      if (res.data.status) {
        setData(res.data.result.map((r, i) => ({ ...r, key: (pg - 1) * lmt + i + 1 })));
        setTotal(res.data.totalCount);
        setSummary(res.data.summary || {});
      }
    } catch {
      notification.error({ message: 'Failed to load report' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => { fetchData(1, limit, filters); }, []);

  const applyFilters = () => { setPage(1); fetchData(1, limit, filters); };
  const resetFilters = () => {
    const blank = { name:'',phone:'',village:'',district:'',bank_account:'',
                    frequency_type:'',member_status:'',continuity_status:'',
                    has_unverified:'',min_months:'' };
    setFilters(blank);
    setPage(1);
    fetchData(1, limit, blank);
  };
  const setF = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));

  const openGapModal = async (record) => {
    setGapMember(record);
    setGapModal(true);
    setGapLoading(true);
    try {
      const res = await axios.get(
        `${baseUrl}/reports/payment-gap-detail/${record.user_id}?bank_account_id=${record.bank_account_id}`,
        { headers }
      );
      if (res.data.status) setGapTimeline(buildTimeline(res.data.result));
    } catch {
      notification.error({ message: 'Failed to load gap detail' });
    } finally {
      setGapLoading(false);
    }
  };

  const [exporting,      setExporting]      = useState(false);
  const [exportModal,    setExportModal]    = useState(false);
  const [exportFrom,     setExportFrom]     = useState(1);
  const [exportTo,       setExportTo]       = useState('');

  const openExportModal = () => {
    setExportFrom(1);
    setExportTo(total);
    setExportModal(true);
  };

  const doExport = async () => {
    const from = Math.max(1, parseInt(exportFrom) || 1);
    const to   = Math.min(parseInt(exportTo) || total, total);
    if (from > to) { notification.warning({ message: '"From" must be less than "To"' }); return; }

    setExporting(true);
    setExportModal(false);
    try {
      const fetchLimit  = to - from + 1;
      const fetchOffset = from - 1;            // backend uses page+limit, so pass as page=1 limit=fetchLimit offset via page trick
      const fetchPage   = 1;
      // Use limit + offset directly: pass limit=fetchLimit and offset via page param math
      // Simpler: fetch page 1 with limit = to, then slice from (from-1)
      const params = new URLSearchParams({ page: 1, limit: to, ...filters });
      const res = await axios.get(`${baseUrl}/reports/payment-continuity?${params}`, { headers });
      if (!res.data.status) { notification.error({ message: 'Export failed' }); return; }

      const sliced = res.data.result.slice(from - 1, to);
      const rows = sliced.map((r, i) => ({
        'S.No':                  from + i,
        'Name':                  r.name,
        'Relation':              r.relation,
        'Father/Husband':        r.father_name,
        'Phone':                 r.phone_no,
        'Village':               r.per_village,
        'Tahsil':                r.per_tahsil,
        'District':              r.per_district,
        'Joining Date':          r.joining_date          ? moment(r.joining_date).format('DD-MM-YYYY')          : '',
        'Member Status':         r.member_status,
        'Bank Account':          r.bank_account,
        'Frequency':             r.frequency_type,
        'Announced Amt':         r.announced_amount,
        'No. of Payments':       r.payment_count,
        'Total Months Paid':     r.total_months_paid,
        'From Month':            r.from_month            ? moment(r.from_month).format('MMM-YYYY')              : '',
        'Upto Month':            r.upto_month            ? moment(r.upto_month).format('MMM-YYYY')              : '',
        'Total Amount':          r.total_amount,
        'Verified Payments':     r.verified_count,
        'Unverified Payments':   r.unverified_count,
        'Unverified Amount':     r.unverified_amount,
        'Continuity Status':     r.continuity_status,
        'Months Since Last':     r.months_since_last,
        'Not Paid Months':       r.not_paid_months,
        'Avg Amt/Payment':       r.avg_payment_amount,
        'Last Txn Date':         r.last_transaction_date ? moment(r.last_transaction_date).format('DD-MM-YYYY') : '',
        'Last Entry Date':       r.last_entry_date       ? moment(r.last_entry_date).format('DD-MM-YYYY')       : '',
        'Avg Entry Delay (days)': r.avg_entry_delay,
        'Late Entries (>7d)':    r.late_entries,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ContinuityReport');
      XLSX.writeFile(wb, `Payment_Continuity_${from}_to_${to}_${moment().format('DDMMYYYY')}.xlsx`);
      notification.success({ message: `Exported ${rows.length} records (${from} to ${to})` });
    } catch {
      notification.error({ message: 'Export failed' });
    } finally {
      setExporting(false);
    }
  };

  // ─── Table Columns ────────────────────────────────────────────────────────
  const columns = [
    {
      title: hdr('S.No'), dataIndex: 'key', key: 'key', fixed: 'left', width: 55,
      render: t => <span style={{ fontWeight: 600, fontSize: 12, color: '#7a0000' }}>{t}</span>,
    },
    {
      title: hdr('Member'), key: 'member', fixed: 'left', width: 220,
      render: r => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#7a0000' }}>{r.name}</div>
          <div style={{ fontSize: 11, color: '#555' }}>{r.relation} — {r.father_name}</div>
        </div>
      ),
    },
    {
      title: hdr('Phone'), dataIndex: 'phone_no', key: 'phone_no', width: 120,
      render: t => <span style={{ fontSize: 12 }}>{t}</span>,
    },
    {
      title: hdr('Village'), dataIndex: 'per_village', key: 'per_village', width: 110,
      render: t => <span style={{ fontSize: 12 }}>{t || '—'}</span>,
    },
    {
      title: hdr('Tahsil'), dataIndex: 'per_tahsil', key: 'per_tahsil', width: 110,
      render: t => <span style={{ fontSize: 12 }}>{t || '—'}</span>,
    },
    {
      title: hdr('District'), dataIndex: 'per_district', key: 'per_district', width: 110,
      render: t => <span style={{ fontSize: 12 }}>{t || '—'}</span>,
    },
    {
      title: hdr('Joining'), dataIndex: 'joining_date', key: 'joining_date', width: 100,
      render: t => t ? moment(t).format('MMM-YYYY') : '—',
    },
    {
      title: hdr('Mem Status'), dataIndex: 'member_status', key: 'member_status', width: 100,
      render: t => <Tag color={t === 'Active' ? 'green' : 'default'}>{t}</Tag>,
    },
    {
      title: hdr('Bank Acc'), dataIndex: 'bank_account', key: 'bank_account', width: 130,
      render: t => <span style={{ fontSize: 12 }}>{t || '—'}</span>,
    },
    {
      title: hdr('Frequency'), dataIndex: 'frequency_type', key: 'frequency_type', width: 100,
      render: t => <span style={{ fontSize: 12 }}>{t || '—'}</span>,
    },
    {
      title: hdr('Announced'), dataIndex: 'announced_amount', key: 'announced_amount', width: 100,
      render: t => t ? <span>₹{Number(t).toLocaleString('en-IN')}</span> : '—',
    },
    {
      title: hdr('Payments'), dataIndex: 'payment_count', key: 'payment_count', width: 90,
      render: t => <span style={{ fontWeight: 600 }}>{t}</span>,
    },
    {
      title: hdr('Months Paid'), dataIndex: 'total_months_paid', key: 'total_months_paid', width: 105,
      render: t => (
        <span style={{ fontWeight: 700, color: '#1890ff', fontSize: 13 }}>{t}</span>
      ),
    },
    {
      title: hdr('From Month'), dataIndex: 'from_month', key: 'from_month', width: 110,
      render: t => t ? moment(t).format('MMM-YYYY') : '—',
    },
    {
      title: hdr('Upto Month'), dataIndex: 'upto_month', key: 'upto_month', width: 110,
      render: t => t ? moment(t).format('MMM-YYYY') : '—',
    },
    {
      title: hdr('Total Amount'), dataIndex: 'total_amount', key: 'total_amount', width: 120,
      render: t => <span style={{ fontWeight: 700 }}>₹{Number(t || 0).toLocaleString('en-IN')}</span>,
    },
    {
      title: hdr('Verified'), dataIndex: 'verified_count', key: 'verified_count', width: 90,
      render: t => <span style={{ color: '#52c41a', fontWeight: 600 }}>{t}</span>,
    },
    {
      title: hdr('Unverified'), dataIndex: 'unverified_count', key: 'unverified_count', width: 100,
      render: (t, r) => t > 0
        ? <Tooltip title={`₹${Number(r.unverified_amount || 0).toLocaleString('en-IN')} pending`}>
            <Badge count={t} style={{ backgroundColor: '#ff4d4f' }} />
          </Tooltip>
        : <CheckCircleOutlined style={{ color: '#52c41a' }} />,
    },
    {
      title: hdr('Unverified Amt'), dataIndex: 'unverified_amount', key: 'unverified_amount', width: 125,
      render: (t, r) => r.unverified_count > 0
        ? <span style={{ color: '#ff4d4f', fontWeight: 600 }}>₹{Number(t || 0).toLocaleString('en-IN')}</span>
        : <span style={{ color: '#52c41a' }}>—</span>,
    },
    {
      title: hdr('Status'), dataIndex: 'continuity_status', key: 'continuity_status', width: 100,
      render: t => t === 'Active'
        ? <Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag>
        : <Tag color="error"   icon={<CloseCircleOutlined />}>Lapsed</Tag>,
    },
    {
      title: hdr('Months Since Last'), dataIndex: 'months_since_last', key: 'months_since_last', width: 130,
      render: (t, r) => r.continuity_status === 'Lapsed'
        ? <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{t} months ago</span>
        : <span style={{ color: '#52c41a' }}>—</span>,
    },
    {
      title: hdr('Not Paid Months'), dataIndex: 'not_paid_months', key: 'not_paid_months', width: 130,
      render: t => t > 0
        ? <Tag color="warning" icon={<WarningOutlined />}>{t} months</Tag>
        : <Tag color="success">None</Tag>,
    },
    {
      title: hdr('Avg Amt/Pay'), dataIndex: 'avg_payment_amount', key: 'avg_payment_amount', width: 115,
      render: t => <span>₹{Number(t || 0).toLocaleString('en-IN')}</span>,
    },
    {
      title: hdr('Announced vs Actual'), key: 'comparison', width: 160,
      render: r => {
        if (!r.announced_amount || !r.avg_payment_amount) return '—';
        const diff = r.avg_payment_amount - r.announced_amount;
        if (Math.abs(diff) < 1) return <Tag color="success">On Track</Tag>;
        return diff > 0
          ? <Tag color="blue">+₹{Number(diff).toLocaleString('en-IN')} above</Tag>
          : <Tag color="warning">₹{Number(Math.abs(diff)).toLocaleString('en-IN')} below</Tag>;
      },
    },
    {
      title: hdr('Last Txn Date'), dataIndex: 'last_transaction_date', key: 'last_transaction_date', width: 120,
      render: t => t ? moment(t).format('DD-MM-YYYY') : '—',
    },
    {
      title: hdr('Entry Date'), dataIndex: 'last_entry_date', key: 'last_entry_date', width: 110,
      render: t => t ? moment(t).format('DD-MM-YYYY') : '—',
    },
    {
      title: hdr('Avg Entry Delay'), dataIndex: 'avg_entry_delay', key: 'avg_entry_delay', width: 125,
      render: t => {
        const days = Number(t || 0);
        if (days <= 2)  return <span style={{ color: '#52c41a' }}>{days}d</span>;
        if (days <= 7)  return <span style={{ color: '#faad14' }}>{days}d</span>;
        return <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{days}d ⚠️</span>;
      },
    },
    {
      title: hdr('Late Entries'), dataIndex: 'late_entries', key: 'late_entries', width: 110,
      render: t => t > 0
        ? <Tag color="warning" icon={<ClockCircleOutlined />}>{t}</Tag>
        : <span style={{ color: '#52c41a' }}>—</span>,
    },
    {
      title: hdr('Gap Detail'), key: 'gap', fixed: 'right', width: 90,
      render: r => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => openGapModal(r)}>
          View
        </Button>
      ),
    },
  ];

  // ─── Gap Modal Timeline ───────────────────────────────────────────────────
  const renderTimeline = () => {
    if (gapLoading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
    if (!gapTimeline.length) return <p>No payment records found.</p>;

    return gapTimeline.map((item, idx) => {
      if (item.type === 'paid') {
        const { data: p } = item;
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%', background: '#52c41a',
              marginTop: 4, marginRight: 12, flexShrink: 0
            }} />
            <div style={{ flex: 1, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '8px 12px' }}>
              <div style={{ fontWeight: 700, color: '#389e0d', fontSize: 13 }}>
                ✅ Paid — {moment(p.start_month).format('MMM-YYYY')} → {moment(p.end_month).format('MMM-YYYY')}
                &nbsp;<span style={{ fontWeight: 400, fontSize: 12 }}>({p.duration_type} month{p.duration_type > 1 ? 's' : ''})</span>
              </div>
              <div style={{ fontSize: 11, color: '#555', marginTop: 3 }}>
                ₹{Number(p.amount).toLocaleString('en-IN')} &nbsp;|&nbsp;
                Txn: {p.transaction_date ? moment(p.transaction_date).format('DD-MMM-YYYY') : '—'} &nbsp;|&nbsp;
                Entered: {p.payment_entry_date ? moment(p.payment_entry_date).format('DD-MMM-YYYY') : '—'} &nbsp;|&nbsp;
                <span style={{ color: p.payment_virify === 'verified' ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
                  {p.payment_virify === 'verified' ? 'Verified' : '⚠️ Unverified'}
                </span>
              </div>
            </div>
          </div>
        );
      }

      if (item.type === 'gap') {
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%', background: '#faad14',
              marginTop: 4, marginRight: 12, flexShrink: 0
            }} />
            <div style={{ flex: 1, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 12px' }}>
              <div style={{ fontWeight: 700, color: '#d48806', fontSize: 13 }}>
                ⚠️ Gap — {item.from} → {item.to}
                &nbsp;<span style={{ fontWeight: 400, fontSize: 12 }}>({item.months} month{item.months > 1 ? 's' : ''} unpaid)</span>
              </div>
            </div>
          </div>
        );
      }

      if (item.type === 'overdue') {
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%', background: '#ff4d4f',
              marginTop: 4, marginRight: 12, flexShrink: 0
            }} />
            <div style={{ flex: 1, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 6, padding: '8px 12px' }}>
              <div style={{ fontWeight: 700, color: '#cf1322', fontSize: 13 }}>
                🔴 Currently Overdue — {item.from} → {item.to}
                &nbsp;<span style={{ fontWeight: 400, fontSize: 12 }}>({item.months} month{item.months > 1 ? 's' : ''} not covered)</span>
              </div>
            </div>
          </div>
        );
      }
      return null;
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: '#7a0000', fontSize: 20, fontWeight: 700 }}>Payment Continuity Report</h2>
        <Button icon={<FileExcelOutlined />} onClick={openExportModal} loading={exporting} style={{ background: '#217346', color: '#fff', border: 'none' }}>
          Export Excel
        </Button>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#e6f7ff', borderRadius: 10 }}>
            <Statistic title="Total Members" value={summary.total_rows || 0} valueStyle={{ color: '#1890ff', fontWeight: 700 }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#f6ffed', borderRadius: 10 }}>
            <Statistic title="Active" value={summary.active_count || 0} valueStyle={{ color: '#52c41a', fontWeight: 700 }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#fff2f0', borderRadius: 10 }}>
            <Statistic title="Lapsed" value={summary.lapsed_count || 0} valueStyle={{ color: '#ff4d4f', fontWeight: 700 }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ background: '#fffbe6', borderRadius: 10 }}>
            <Statistic
              title="Unverified Amount"
              value={summary.total_unverified_amount || 0}
              prefix="₹"
              valueStyle={{ color: '#faad14', fontWeight: 700 }}
              formatter={v => Number(v).toLocaleString('en-IN')}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 16, borderRadius: 10 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={8} md={6}>
            <Input placeholder="Name" value={filters.name}
              prefix={<SearchOutlined />}
              onChange={e => setF('name', e.target.value)}
              onPressEnter={applyFilters} allowClear />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Input placeholder="Phone" value={filters.phone}
              prefix={<SearchOutlined />}
              onChange={e => setF('phone', e.target.value)}
              onPressEnter={applyFilters} allowClear />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Input placeholder="Village" value={filters.village}
              onChange={e => setF('village', e.target.value)}
              onPressEnter={applyFilters} allowClear />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Input placeholder="District" value={filters.district}
              onChange={e => setF('district', e.target.value)}
              onPressEnter={applyFilters} allowClear />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select placeholder="Bank Account" value={filters.bank_account || undefined}
              onChange={v => setF('bank_account', v)} allowClear style={{ width: '100%' }}>
              {bankAccounts.map(a => <Option key={a.id} value={String(a.id)}>{a.account_name}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select placeholder="Continuity Status" value={filters.continuity_status || undefined}
              onChange={v => setF('continuity_status', v)} allowClear style={{ width: '100%' }}>
              <Option value="Active">Active</Option>
              <Option value="Lapsed">Lapsed</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select placeholder="Has Unverified" value={filters.has_unverified || undefined}
              onChange={v => setF('has_unverified', v)} allowClear style={{ width: '100%' }}>
              <Option value="yes">Yes — Has Unverified</Option>
              <Option value="no">No — All Verified</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select placeholder="Frequency Type" value={filters.frequency_type || undefined}
              onChange={v => setF('frequency_type', v)} allowClear style={{ width: '100%' }}>
              <Option value="Monthly">Monthly</Option>
              <Option value="Quarterly">Quarterly</Option>
              <Option value="Yearly">Yearly</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select placeholder="Member Status" value={filters.member_status || undefined}
              onChange={v => setF('member_status', v)} allowClear style={{ width: '100%' }}>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Input type="number" placeholder="Min Months Paid (e.g. 12)"
              value={filters.min_months}
              onChange={e => setF('min_months', e.target.value)}
              onPressEnter={applyFilters} allowClear />
          </Col>
          <Col xs={24} sm={8} md={6} style={{ display: 'flex', gap: 8 }}>
            <Button type="primary" onClick={applyFilters} style={{ flex: 1 }}>Search</Button>
            <Button onClick={resetFilters} style={{ flex: 1 }}>Reset</Button>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false} style={{ borderRadius: 10 }}>
        <Spin spinning={loading}>
          <Table
            dataSource={data}
            columns={columns}
            rowKey="key"
            scroll={{ x: 3200 }}
            size="small"
            pagination={{
              current: page,
              pageSize: limit,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t} records`,
              onChange: (pg, lmt) => { setPage(pg); setLimit(lmt); fetchData(pg, lmt, filters); },
            }}
          />
        </Spin>
      </Card>

      {/* Export Range Modal */}
      <Modal
        title={<span style={{ fontWeight: 700 }}>Export to Excel — Select Range</span>}
        open={exportModal}
        onCancel={() => setExportModal(false)}
        onOk={doExport}
        okText="Download Excel"
        okButtonProps={{ style: { background: '#217346', borderColor: '#217346' } }}
        width={380}
      >
        <div style={{ marginBottom: 8, color: '#555', fontSize: 13 }}>
          Total records available: <strong>{total}</strong>
        </div>
        <Row gutter={12} align="middle">
          <Col span={11}>
            <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 600 }}>From (S.No)</div>
            <Input
              type="number" min={1} max={total}
              value={exportFrom}
              onChange={e => setExportFrom(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={2} style={{ textAlign: 'center', paddingTop: 20 }}>—</Col>
          <Col span={11}>
            <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 600 }}>To (S.No)</div>
            <Input
              type="number" min={1} max={total}
              value={exportTo}
              onChange={e => setExportTo(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
          Will export <strong>{Math.max(0, (parseInt(exportTo) || 0) - (parseInt(exportFrom) || 0) + 1)}</strong> records
          &nbsp;— active filters will be applied.
        </div>
      </Modal>

      {/* Gap Detail Modal */}
      <Modal
        title={gapMember ? (
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{gapMember.name} — Payment Gap Analysis</div>
            <div style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>
              {gapMember.relation} | {gapMember.phone_no} | {gapMember.bank_account}
            </div>
          </div>
        ) : 'Gap Analysis'}
        open={gapModal}
        onCancel={() => { setGapModal(false); setGapTimeline([]); setGapMember(null); }}
        footer={null}
        width={620}
      >
        {gapMember && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card size="small" style={{ background: '#f6ffed', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#555' }}>Months Paid</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#52c41a' }}>{gapMember.total_months_paid}</div>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ background: '#fff2f0', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#555' }}>Not Paid</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#ff4d4f' }}>{gapMember.not_paid_months}</div>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ background: '#fffbe6', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#555' }}>Unverified</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#faad14' }}>{gapMember.unverified_count}</div>
              </Card>
            </Col>
          </Row>
        )}
        <Divider style={{ margin: '8px 0 16px' }} />
        <div style={{ maxHeight: 440, overflowY: 'auto', paddingRight: 4 }}>
          {renderTimeline()}
        </div>
      </Modal>
    </div>
  );
}
